import { useCallback } from 'react';
import {
  Building2,
  CreditCard,
  LayoutDashboard,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import type { Papel } from '@social/shared';
import { useSessao } from '../lib/sessao';

export interface ItemMenu {
  para: string;
  /**
   * Chave ESTÁVEL da tela.
   *
   * Não é a rota. A rota muda quando a navegação é reorganizada, e usar a rota
   * como chave faria toda concessão de acesso gravada apontar para o vazio no
   * dia da reorganização — o acesso sumiria em silêncio, sem erro nenhum.
   */
  chave: string;
  rotulo: string;
  icone?: LucideIcon;
  /** Ausente = todo papel vê. */
  papeis?: readonly Papel[];
}

export interface GrupoMenu {
  titulo: string;
  itens: ItemMenu[];
}

export const MENU: GrupoMenu[] = [
  {
    titulo: 'PAINEL',
    itens: [
      {
        para: '/',
        chave: 'inicio',
        rotulo: 'Início',
        icone: LayoutDashboard,
      },
    ],
  },
  {
    titulo: 'CADASTRO',
    itens: [
      {
        para: '/sociedades',
        chave: 'sociedades',
        rotulo: 'Sociedades',
        icone: Building2,
      },
      {
        para: '/pessoas',
        chave: 'pessoas',
        rotulo: 'Sócios e administradores',
        icone: Users,
      },
    ],
  },
  {
    titulo: 'ESCRITÓRIO',
    itens: [
      {
        para: '/usuarios',
        chave: 'usuarios',
        rotulo: 'Equipe',
        icone: UsersRound,
        // Só administrador: a tela cria conta, muda papel e DEFINE senha de
        // outra pessoa. Diferente da `Assinatura`, aqui esconder é o certo —
        // não há mensagem no produto que aponte para cá pedindo que um
        // assistente a abra.
        papeis: ['administrador'],
      },
      {
        para: '/assinatura',
        chave: 'assinatura',
        rotulo: 'Assinatura',
        icone: CreditCard,
        // Quem NÃO é administrador vê a tela (precisa saber o que o plano
        // permite, e por que esbarrou num limite) — só não troca de plano.
        // Esconder o item faria a mensagem "limite do plano" apontar para
        // uma tela que a pessoa não consegue abrir.
      },
    ],
  },
  // F2 — rota /atos ainda não existe no App.tsx.
  // {
  //   titulo: 'ATOS',
  //   itens: [
  //     {
  //       para: '/atos',
  //       chave: 'atos',
  //       rotulo: 'Atos societários',
  //       icone: FileText,
  //       // O ato é peça jurídica assinada por quem tem registro no CRC.
  //       papeis: ['administrador', 'contador'],
  //     },
  //   ],
  // },
];

/** Caixa alta e sem acento, para o filtro achar "sócio" digitando "socio". */
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Quem pode ver o item, a partir do papel.
 *
 * A FORMA — uma função que recebe o item e devolve booleano — é a mesma do
 * Adonai, que resolve isto com matriz de permissões e acesso por tela. Aqui a
 * implementação é o papel, porque é o que existe. A costura fica pronta para a
 * F1d trazer permissões sem mexer no `Shell` nem na paleta.
 */
export function podeVerPeloPapel(papel: Papel) {
  return (item: Pick<ItemMenu, 'papeis'>): boolean =>
    !item.papeis || item.papeis.includes(papel);
}

/**
 * Ponto ÚNICO de onde vem a visibilidade do menu.
 *
 * `Shell` e `PaletaComandos` chamam só isto — nenhum dos dois sabe que hoje a
 * resposta vem do papel. Quando a F1d trouxer matriz de permissão + acesso
 * por tela, `podeVerPeloPapel(papel)` deixa de bastar (ganha um segundo
 * argumento, ou vira outra função inteira) e só este hook muda; sem ele, a
 * troca seria em três lugares — a função e os dois pontos de chamada.
 *
 * `podeVerPeloPapel` continua exportada e testada direto: é a função pura,
 * o hook é só a fiação até a sessão.
 */
export function usePodeVer(): (item: ItemMenu) => boolean {
  const papel = useSessao((s) => s.papel);
  return useCallback(
    (item: ItemMenu) => (papel ? podeVerPeloPapel(papel)(item) : false),
    [papel],
  );
}

/**
 * Filtra o menu por texto e por visibilidade.
 *
 * Grupo que fica sem item NÃO aparece: cabeçalho vazio é ruído, e o usuário
 * procura debaixo dele sem achar nada.
 */
export function filtrarMenu(
  filtro: string,
  podeVer: (item: ItemMenu) => boolean,
): GrupoMenu[] {
  const busca = normalizar(filtro.trim());
  return MENU.map((grupo) => ({
    titulo: grupo.titulo,
    itens: grupo.itens.filter(
      (i) => podeVer(i) && (busca === '' || normalizar(i.rotulo).includes(busca)),
    ),
  })).filter((g) => g.itens.length > 0);
}
